import { apiService } from './api';

export interface ShareLinkResponse {
  slug: string;
  path: string;
  hasTemporaryAttachments?: boolean;
  seoEnabled: boolean;
  protectedEnabled: boolean;
  seoTitle: string;
  seoHeading: string;
  seoDescription: string;
}

export interface SharedAnswerUrl {
  url: string;
  hasTemporaryAttachments?: boolean;
  seoEnabled: boolean;
  protectedEnabled: boolean;
  seoTitle: string;
  seoHeading: string;
  seoDescription: string;
}

export type ShareMetadataSuggestionField = 'slug' | 'seoTitle' | 'seoDescription';

export interface ShareMetadataSuggestionResponse {
  field: ShareMetadataSuggestionField;
  value: string;
}

export interface SharedAnswerResponse {
  question: string;
  answer: string;
  createdAt: string;
  questionCreatedAt: string | null;
  locale: string | null;
  botProfileCode: string | null;
  hasTemporaryAttachments?: boolean;
  seoEnabled: boolean;
  protectedEnabled: boolean;
  seoTitle: string;
  seoHeading: string | null;
  seoDescription: string;
  seoActivatedAt: string | null;
  isRestrictedPreview: boolean;
}

export interface SharedAnswerExample {
  slug: string;
  question: string;
  customHeading: string | null;
  rating: number | null;
}

export type CreateSharedAnswerOptions = {
  seoIntent?: 'enable' | 'disable';
  protectionIntent?: 'enable' | 'disable';
  defaultIntent?: boolean;
  customSlug?: string;
  seoTitle?: string;
  seoDescription?: string;
};

export async function createSharedAnswerUrl(
  messageId: string,
  locale: 'en' | 'fr',
  options: CreateSharedAnswerOptions = {},
): Promise<SharedAnswerUrl> {
  const normalizedMessageId = Number(messageId);
  if (!Number.isInteger(normalizedMessageId) || normalizedMessageId <= 0) {
    throw new Error('A persisted answer id is required to create a share link.');
  }

  const response = await apiService.post<ShareLinkResponse>('/bot/share', {
    id: normalizedMessageId,
    locale,
    seoIntent: options.seoIntent,
    protectionIntent: options.protectionIntent,
    defaultIntent: options.defaultIntent,
    customSlug: options.customSlug,
    seoTitle: options.seoTitle,
    seoDescription: options.seoDescription,
  });

  return {
    url: `${window.location.origin}${response.path}`,
    ...(response.hasTemporaryAttachments === true ? { hasTemporaryAttachments: true } : {}),
    seoEnabled: response.seoEnabled,
    protectedEnabled: response.protectedEnabled,
    seoTitle: response.seoTitle,
    seoHeading: response.seoHeading,
    seoDescription: response.seoDescription,
  };
}

export async function generateSharedAnswerMetadataSuggestion(
  messageId: string,
  field: ShareMetadataSuggestionField,
  locale: 'en' | 'fr',
): Promise<ShareMetadataSuggestionResponse> {
  const normalizedMessageId = Number(messageId);
  if (!Number.isInteger(normalizedMessageId) || normalizedMessageId <= 0) {
    throw new Error('A persisted answer id is required to generate share metadata.');
  }

  return apiService.post<ShareMetadataSuggestionResponse>('/bot/share/suggestion', {
    id: normalizedMessageId,
    field,
    locale,
  });
}

export async function fetchSharedAnswer(slug: string): Promise<SharedAnswerResponse> {
  return apiService.get<SharedAnswerResponse>(
    `/public/shared-answer/${encodeURIComponent(slug)}`,
    undefined,
    true,
  );
}

export async function fetchSharedAnswerExamples(
  locale: 'en' | 'fr',
): Promise<SharedAnswerExample[]> {
  return apiService.get<SharedAnswerExample[]>(
    `/public/shared-answer-examples?locale=${encodeURIComponent(locale)}`,
    undefined,
    true,
  );
}
